<template>
  <div class="flex flex-column flex-gap-1">
    <div class="flex flex-row flex-gap-1">
      <InputWrapper
        v-slot="{ inputId }"
        :input-id="`${uid}-pickup-date-select`"
        :label="t('parcelScheduling.pickupDate')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="pickupDate"
          :options="pickupDateOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('parcelScheduling.pickupDate')"
          append-to="body"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ inputId }"
        :input-id="`${uid}-pickup-time-earliest-select`"
        :label="t('parcelScheduling.pickupTimeEarliest')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="pickupTimeEarliest"
          :options="pickupTimeEarliestOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('parcelScheduling.pickupTimeEarliest')"
          append-to="body"
        />
      </InputWrapper>
      <InputWrapper
        v-slot="{ inputId }"
        :input-id="`${uid}-pickup-time-latest-select`"
        :label="t('parcelScheduling.pickupTimeLatest')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="pickupTimeLatest"
          :options="pickupTimeLatestOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('parcelScheduling.pickupTimeLatest')"
          append-to="body"
        />
      </InputWrapper>
    </div>
    <div>
      <ShipmentSearchResultView
        v-model:columns="columns"
        v-model:selection="selectedShipments"
        :shipment-search-criteria="criteria"
        :results="results"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import { PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';
import {
  ColumnSpecification,
  DropdownOption,
  Loadable,
} from '@rygen/rygen-ui/model';

import {
  ShipmentSearchCriteria,
  ShipmentSearchData,
  ShipmentSearchResult,
} from '@/model';

import ShipmentSearchResultView from '@/components/shipment/ShipmentSearchResult.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const pickupDate = defineModel('pickupDate', {
  type: Object as PropType<DateTime>,
  default: undefined,
});

const pickupTimeEarliest = defineModel('pickupTimeEarliest', {
  type: Object as PropType<DateTime>,
  default: undefined,
});

const pickupTimeLatest = defineModel('pickupTimeLatest', {
  type: Object as PropType<DateTime>,
  default: undefined,
});

const selectedShipments = defineModel('selectedShipments', {
  type: Array as PropType<ShipmentSearchData[]>,
  default: () => [],
});

const columns = defineModel('columns', {
  type: Array as PropType<ColumnSpecification[]>,
  default: () => [],
});

const props = defineProps({
  criteria: {
    type: ShipmentSearchCriteria,
    required: true,
  },
  pickupDateOptions: {
    type: Array as PropType<DropdownOption<DateTime | undefined>[]>,
    default: () => [],
  },
  pickupTimeEarliestOptions: {
    type: Array as PropType<DropdownOption<DateTime | undefined>[]>,
    default: () => [],
  },
  pickupTimeLatestOptions: {
    type: Array as PropType<DropdownOption<DateTime | undefined>[]>,
    default: () => [],
  },
  results: {
    type: Object as PropType<Loadable<ShipmentSearchResult>>,
    default: () => ({ loading: false }),
  },
});

const {
  criteria,
  pickupDateOptions,
  pickupTimeEarliestOptions,
  pickupTimeLatestOptions,
  results,
} = toRefs(props);

const { t } = useI18n();
const uid = useUid('parcel-appointment-selection-phase');
</script>
